import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAssignmentColumnPref, ISavedAssignmentFilter, IUpdateUserSettingsRequest, IUserSettings } from '../models/user-settings';
import { UserSettingsService } from './user-settings.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class UserSettingsStoreService {

  constructor(
    private userSettingsService: UserSettingsService,
    private appNotificationService: AppNotificationService,
  ) { }

  private _userSettings$ = new BehaviorSubject<IUserSettings | null>(null);
  public userSettings$ = this._userSettings$.asObservable();

  getCurrentUserSettings() {
    this.userSettingsService.getCurrentUserSettings()
      .pipe(untilDestroyed(this))
      .subscribe(settings => this._userSettings$.next(this.normalize(settings)));
  }

  /**
   * Replaces the entire settings blob on the server and updates the local store.
   * The backend accepts a full replace for both lists — frontend holds the source
   * of truth while editing and pushes the final shape.
   */
  updateCurrentUserSettings(request: IUpdateUserSettingsRequest, successMessage?: string) {
    this.userSettingsService.updateCurrentUserSettings(request)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const current = this._userSettings$.value;
        if (current) {
          this._userSettings$.next({
            ...current,
            assignmentFilters: request.assignmentFilters,
            assignmentColumns: request.assignmentColumns,
            lastAppliedAssignmentFilterId: request.lastAppliedAssignmentFilterId ?? null,
          });
        } else {
          this.getCurrentUserSettings();
        }
        if (successMessage) {
          this.appNotificationService.notification$.next({ message: successMessage, style: 'success' });
        }
      });
  }

  // Convenience helpers used by the assignments page

  saveAssignmentFilter(filter: ISavedAssignmentFilter) {
    const current = this._userSettings$.value;
    const filters = current?.assignmentFilters ?? [];
    const idx = filters.findIndex(f => f.id === filter.id);
    const next = idx >= 0
      ? filters.map((f, i) => i === idx ? filter : f)
      : [...filters, filter];
    this.updateCurrentUserSettings({
      assignmentFilters: next,
      assignmentColumns: current?.assignmentColumns ?? [],
      lastAppliedAssignmentFilterId: current?.lastAppliedAssignmentFilterId ?? null,
    }, 'Filter gespeichert');
  }

  deleteAssignmentFilter(id: string) {
    const current = this._userSettings$.value;
    if (!current) return;
    const next = current.assignmentFilters.filter(f => f.id !== id);
    // Clear the last-applied pointer if it referenced the deleted filter.
    const lastApplied = current.lastAppliedAssignmentFilterId === id
      ? null
      : (current.lastAppliedAssignmentFilterId ?? null);
    this.updateCurrentUserSettings({
      assignmentFilters: next,
      assignmentColumns: current.assignmentColumns,
      lastAppliedAssignmentFilterId: lastApplied,
    }, 'Filter gelöscht');
  }

  updateAssignmentColumns(columns: IAssignmentColumnPref[]) {
    const current = this._userSettings$.value;
    this.updateCurrentUserSettings({
      assignmentFilters: current?.assignmentFilters ?? [],
      assignmentColumns: columns,
      lastAppliedAssignmentFilterId: current?.lastAppliedAssignmentFilterId ?? null,
    });
  }

  setLastAppliedAssignmentFilter(id: string | null) {
    const current = this._userSettings$.value;
    if (!current) return;
    // Skip the PUT when nothing changed — prevents redundant server writes
    // during the initial auto-apply after login.
    if ((current.lastAppliedAssignmentFilterId ?? null) === id) return;
    this.updateCurrentUserSettings({
      assignmentFilters: current.assignmentFilters,
      assignmentColumns: current.assignmentColumns,
      lastAppliedAssignmentFilterId: id,
    });
  }

  private normalize(settings: IUserSettings): IUserSettings {
    return {
      ...settings,
      assignmentFilters: settings.assignmentFilters ?? [],
      assignmentColumns: settings.assignmentColumns ?? [],
      lastAppliedAssignmentFilterId: settings.lastAppliedAssignmentFilterId ?? null,
    };
  }
}
